import { create } from 'zustand';
import type { EntitlementRequiredError, InsufficientCreditsError } from '../types/errors';
import { trackGaEvent } from '../utils/ga';
import { gaCreditFeature } from '../utils/gaCreditFeature';
import { onSessionChange } from './session-read';

/**
 * Mode and detail payload for the global UpgradePrompt modal.
 * - 'credits' → 402 / insufficient_credits (free user out of credits)
 * - 'plan' → 403 / entitlement_required (free user lacks plan-gated feature)
 *
 * Distinct copy + CTA per mode. The axios interceptor in services/api.ts
 * is the only producer of these events; components consume by reading
 * the store.
 */
export type UpgradePromptMode = 'credits' | 'plan';

interface UpgradePromptState {
  open: boolean;
  mode: UpgradePromptMode | null;
  // Discriminated by mode at the call site; loose typing here keeps the
  // store small and avoids cross-importing the Banker exception types
  // for every consumer.
  details: InsufficientCreditsError['details'] | EntitlementRequiredError['details'] | null;
  /**
   * The plan selector opened by "See plans". It replaces the prompt on
   * screen, so it lives here rather than in the modal's local state: Back
   * and Escape (useGateOpen / closeOpenGate) must see it as the gate it is.
   */
  plansOpen: boolean;
  show: (mode: UpgradePromptMode, details: UpgradePromptState['details']) => void;
  /** Close the prompt (the plan selector, if up, stays). */
  close: () => void;
  /** "See plans": the prompt gives way to the plan selector. */
  openPlans: () => void;
  /** Close the plan selector, and the prompt with it. */
  closePlans: () => void;
}

/**
 * THE monetization event (docs/plans/GA-INSTRUMENTATION.md): which feature's
 * credit exhaustion creates upgrade pressure, joined against
 * begin_checkout/subscribe for per-trigger paywall conversion.
 *
 * Fired HERE, where the global prompt opens in its credits mode, once per
 * open (a second 402 while it is already up is the same refusal on screen,
 * even when a different feature sent it), with the surface the server
 * refused: `details.feature` mapped onto the event's one vocabulary by
 * gaCreditFeature (utils/gaCreditFeature.ts, the source of truth; a missing
 * or unknown key is misc_ai). David's decision, review round 2 q10
 * (2026-10-24); before, only the Needs Guesser page counted a refusal this
 * prompt answered.
 */
function trackOutOfCredits(details: UpgradePromptState['details']): void {
  const feature = (details as { feature?: unknown } | null)?.feature;
  trackGaEvent('out_of_credits', { feature: gaCreditFeature(feature) });
}

export const useUpgradePromptStore = create<UpgradePromptState>((set, get) => ({
  open: false,
  mode: null,
  details: null,
  plansOpen: false,
  show: (mode, details) => {
    const was = get();
    const opensCredits = mode === 'credits' && !(was.open && was.mode === 'credits');
    set({ open: true, mode, details });
    if (opensCredits) trackOutOfCredits(details);
  },
  close: () => set({ open: false }),
  openPlans: () => set({ open: false, plansOpen: true }),
  closePlans: () => set({ open: false, plansOpen: false }),
}));

// A prompt (or its plan selector) raised for one session closes when the
// session changes (#940).
onSessionChange(() => useUpgradePromptStore.getState().closePlans());
